import React from 'react';
import L from 'leaflet';

import { calculateOrientationShiftedPosition, cellMarkersReprPolySizesPerZoom } from 'utils/Map/mapUtils';

export default class LeafletCellMarkerReprPoly extends L.Polygon {
  private cellMarkerInfo: any;
  private currentSize: number;
  private highlightedCriteriasRef: React.RefObject<any>;
  private clickedCellRef: React.RefObject<any>;

  constructor(
    cellMarkerInfo: any,
    highlightedCriteriasRef: React.RefObject<any>,
    clickedCellRef: React.RefObject<any>,
    options?: L.PolylineOptions
  ) {
    //fixed settings
    const baseOptions = {
      stroke: false, // dnot draw border
      weight: 3, // line weight , used only for highlight
      color: 'red', // line color, used only for highlight
      opacity: 0.8, // for highlight
      fillOpacity: 0.5,
      fillColor: cellMarkerInfo.cellBandColor,
    };

    super([], { ...baseOptions, ...options });
    this.cellMarkerInfo = cellMarkerInfo;
    this.currentSize = 30; // somehow default value
    this.highlightedCriteriasRef = highlightedCriteriasRef;
    this.clickedCellRef = clickedCellRef;

    this.on('pm:enable', (e: any) => e.target.pm.disable());
  }

  // Called when added to the map
  onAdd(map: L.Map) {
    // Call the parent class's onAdd method first
    super.onAdd(map);
    // Calculate and set triangle vertices
    this._updateVertices();
    this.verifyHighlightCriteria();
    this._map.on('zoomend', this._updateVertices, this);
    return this;
  }

  onRemove(map: L.Map) {
    this._map.off('zoomend', this._updateVertices, this);
    super.onRemove(map);
    return this;
  }

  verifyHighlightCriteria() {
    const needsHighlight = this.highlightedCriteriasRef.current?.some(
      (highlightedCriteria: any) =>
        this.cellMarkerInfo[highlightedCriteria.id] &&
        this.cellMarkerInfo[highlightedCriteria.id] ===
          highlightedCriteria.value
    );

    const isCellClicked = this.clickedCellRef.current && this.clickedCellRef.current.id === this.cellMarkerInfo.id;
    const styles = {
      stroke: needsHighlight,
      color: 'red',
      ...(isCellClicked && {
        stroke: true,
        color: 'blue',
      }),
    };

    this.setStyle(styles);
  }

  _updateVertices() {
    const map = this._map;
    const zoomLevel = map.getZoom();

    const squareSize = cellMarkersReprPolySizesPerZoom[zoomLevel]/1.4 || this.currentSize/1.4;

    this.currentSize =
      cellMarkersReprPolySizesPerZoom[zoomLevel] || this.currentSize;
    const cellShiftedLatLng = calculateOrientationShiftedPosition(
      map,
      this.currentSize,
      this.cellMarkerInfo.location,
      this.cellMarkerInfo.orientation,
      this.cellMarkerInfo.cellIndexWithinOrientation
    );
    const centerPoint = map.latLngToLayerPoint(cellShiftedLatLng);

    const pointsRepr = this.cellMarkerInfo.TECHNOLOGY === 'LTE' ? this._calculateEquilateralTriangleInPixels(
      centerPoint,
      this.currentSize,
      this.cellMarkerInfo.orientation
    ) : this._calculateRectangleInPixels(
      centerPoint,
      squareSize,
      this.cellMarkerInfo.orientation
    );

    const triangleVerticesLatLng = pointsRepr.map((vertex) =>
      map.layerPointToLatLng(vertex)
    );

    this.setLatLngs(triangleVerticesLatLng);
  }

  _calculateEquilateralTriangleInPixels(
    centerPoint: L.Point,
    sizePixels: number,
    rotationAngle: number
  ) {
    const triangleSide = (sizePixels * Math.sqrt(3)) / 2;
    const height = (Math.sqrt(3) / 2) * triangleSide;

    const vertices = [
      L.point(centerPoint.x, centerPoint.y + (2 * height) / 3),
      L.point(centerPoint.x - triangleSide / 2, centerPoint.y - height / 3),
      L.point(centerPoint.x + triangleSide / 2, centerPoint.y - height / 3),
    ];

    const rotatedVertices = vertices.map((vertex) =>
      this._rotatePointInPixels(vertex, centerPoint, rotationAngle)
    );
    return rotatedVertices;
  }

  _calculateRectangleInPixels(
    centerPoint: L.Point,
    squareSize: number,
    rotationAngle: number
  ) {
    const halfWidth = squareSize / 2;
  
    // Define corners relative to the center
    const corners = [
      L.point(centerPoint.x - halfWidth, centerPoint.y - halfWidth), // Top-left
      L.point(centerPoint.x + halfWidth, centerPoint.y - halfWidth), // Top-right
      L.point(centerPoint.x + halfWidth, centerPoint.y + halfWidth), // Bottom-right
      L.point(centerPoint.x - halfWidth, centerPoint.y + halfWidth), // Bottom-left
    ];
  
    // Rotate corners around the center
    const rotatedCorners = corners.map((corner) =>
      this._rotatePointInPixels(corner, centerPoint, rotationAngle)
    );
    return rotatedCorners;
  }

  _rotatePointInPixels(point: L.Point, center: L.Point, angle: number) {
    const rad = (angle * Math.PI) / 180;
    const x = point.x - center.x;
    const y = point.y - center.y;

    const rotatedX = x * Math.cos(rad) - y * Math.sin(rad);
    const rotatedY = x * Math.sin(rad) + y * Math.cos(rad);

    return L.point(rotatedX + center.x, rotatedY + center.y);
  }
}
